import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import type { MetadataIcon } from "@cohesive/api-client";
import { Icon, IconSelector } from "@cohesive/ui-components";
import { Folder } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const DESCRIPTION_MAX = 280;

export interface WorkspaceFormValues {
  name: string;
  icon: MetadataIcon | null;
  description: string;
}

export interface WorkspaceFormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Overrides the default "Save" submit button label. */
  submitLabel?: string;
  /** Overrides the default "Saving..." pending label. */
  pendingLabel?: string;
  /** Submit is disabled until at least one of these is true. For
   *  "create" pass `true` to allow submitting whenever the name is
   *  non-empty; for "edit" pass false unless the user actually changed
   *  something. */
  alwaysAllowSubmit?: boolean;
  isPending: boolean;
  initialValues?: Partial<WorkspaceFormValues>;
  onSubmit: (values: WorkspaceFormValues) => void;
}

/**
 * Shared dialog for creating or editing a workspace. Owns the form
 * fields (icon picker, name, description) and dirty-tracking; the
 * caller plugs in the mutation + success handling.
 *
 * Chrome matches RenameCanvasDialog: DialogDescription under the title,
 * py-4 body padding, and standardized "Save" / "Saving..." footer.
 */
export function WorkspaceFormDialog({
  open,
  onClose,
  title,
  description,
  submitLabel = "Save",
  pendingLabel = "Saving...",
  alwaysAllowSubmit = false,
  isPending,
  initialValues,
  onSubmit,
}: WorkspaceFormDialogProps) {
  const initial = useMemo<WorkspaceFormValues>(
    () => ({
      name: initialValues?.name ?? "",
      icon: initialValues?.icon ?? null,
      description: initialValues?.description ?? "",
    }),
    // `icon` is a `MetadataIcon | null` object literal that the parent
    // typically reconstructs on every render — listing it directly here
    // would make `initial` recompute every render. We bucket it via a
    // structural fingerprint so the memo only invalidates on a *real*
    // change (icon swap), not a reference churn.
    [
      initialValues?.name,
      initialValues?.description,
      JSON.stringify(initialValues?.icon ?? null),
    ],
  );

  const [name, setName] = useState(initial.name);
  const [icon, setIcon] = useState<MetadataIcon | null>(initial.icon);
  const [formDescription, setFormDescription] = useState(initial.description);
  const [iconOpen, setIconOpen] = useState(false);

  // Re-seed when the dialog transitions from closed → open. Reading
  // `initial` through a ref keeps the effect's deps to `[open]` only,
  // so a parent re-render that hands us a fresh `initialValues`
  // object literal mid-edit can't blow away the user's in-progress
  // input. (When the dialog reopens for a different target, the new
  // values are picked up via the ref at that moment.)
  const initialRef = useRef(initial);
  initialRef.current = initial;
  useEffect(() => {
    if (open) {
      setName(initialRef.current.name);
      setIcon(initialRef.current.icon);
      setFormDescription(initialRef.current.description);
      setIconOpen(false);
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedDescription = formDescription.trim();
  const isDirty =
    trimmedName !== initial.name.trim() ||
    JSON.stringify(icon) !== JSON.stringify(initial.icon) ||
    trimmedDescription !== initial.description.trim();

  const canSubmit =
    !!trimmedName && !isPending && (alwaysAllowSubmit || isDirty);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ name: trimmedName, icon, description: trimmedDescription });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isPending) onClose();
      }}
    >
      {/** `overflow-visible` is intentionally kept (unlike the plain
       *   RenameCanvasDialog DialogContent) because the inline icon-picker
       *   popover must paint outside the dialog's bounds. The default
       *   DialogContent `overflow-y-auto` would clip it on all sides. */}
      <DialogContent className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="py-4 space-y-3">
          <div className="flex gap-2">
            <Popover open={iconOpen} onOpenChange={setIconOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Choose workspace icon"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center
                             rounded-md border border-input bg-background hover:bg-accent
                             transition-colors cursor-pointer
                             focus-visible:outline-hidden focus-visible:ring-2
                             focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {icon ? (
                    <Icon icon={icon} size={20} />
                  ) : (
                    <Folder className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </PopoverTrigger>
              {/** `disablePortal` keeps the popover inside the parent
               *   Dialog's DOM tree. A portalled popover becomes a
               *   sibling of the dialog content, and Radix's modal
               *   Dialog applies `aria-hidden` / `inert` to its
               *   siblings while open — the popover would render but
               *   every click inside it (emoji buttons, color swatches)
               *   would be swallowed before reaching its handler. The
               *   dialog's focus scope would also yank focus back out
               *   of the popover, so the search input couldn't be
               *   typed in. Inline rendering sidesteps both. */}
              <PopoverContent
                align="start"
                disablePortal
                className="p-0 w-[352px]"
              >
                <IconSelector
                  selectedIcon={icon ?? undefined}
                  onIconChange={(next) => setIcon(next ?? null)}
                />
              </PopoverContent>
            </Popover>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Workspace name"
              className="h-10"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
            />
          </div>
          <div className="space-y-1">
            <Textarea
              value={formDescription}
              onChange={(e) =>
                setFormDescription(e.target.value.slice(0, DESCRIPTION_MAX))
              }
              placeholder="Describe what this workspace is for (optional)"
              rows={3}
              maxLength={DESCRIPTION_MAX}
              className="resize-none"
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/70">
                {formDescription.length}/{DESCRIPTION_MAX}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isPending ? pendingLabel : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
